import { hmac } from '@noble/hashes/hmac.js';
import { sha256 } from '@noble/hashes/sha2.js';

const hexToBytes = (hex: string): Uint8Array => {
  const clean = hex.startsWith('0x') ? hex.slice(2) : hex;
  if (clean.length % 2 !== 0) throw new Error('imgproxy secret must be hex-encoded');
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i += 1) {
    out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  }
  return out;
};

const bytesToBase64Url = (bytes: Uint8Array): string => {
  // We build a binary string explicitly rather than `String.fromCharCode(...bytes)`
  // because spreading a 32+ byte Uint8Array hits call-stack limits in some engines.
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/, '');
};

const stringToBase64Url = (str: string): string =>
  bytesToBase64Url(new TextEncoder().encode(str));

export type ImgproxyOptions = {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'png' | 'jpg' | 'avif';
  fit?: 'fit' | 'fill' | 'auto';
};

export type ImgproxyConfig = {
  baseUrl: string;
  keyHex: string;
  saltHex: string;
};

/**
 * Build a signed imgproxy URL for a source image.
 *
 * Format:
 *   <baseUrl>/<signature>/<processing options>/<base64url source>
 *
 * Signature is HMAC-SHA256(key, salt || path_after_signature), truncated per
 * imgproxy's IMGPROXY_SIGNATURE_SIZE (default 32 bytes = full SHA-256).
 *
 * Source is base64url-encoded (not `plain/...`) so nginx merge_slashes cannot
 * collapse `//` in `https://` and corrupt the URL for imgproxy.
 */
export const buildImgproxyUrl = (
  config: ImgproxyConfig,
  source: string,
  options: ImgproxyOptions = {}
): string => {
  const { baseUrl, keyHex, saltHex } = config;
  if (!baseUrl || !keyHex || !saltHex) {
    throw new Error('imgproxy: baseUrl, key and salt are required');
  }

  const width = options.width ?? 0;
  const height = options.height ?? 0;
  const fit = options.fit ?? 'fill';
  const quality = options.quality ?? 70;
  const format = options.format ?? 'webp';

  const processing: string[] = [];
  if (width > 0 || height > 0) processing.push(`rs:${fit}:${width}:${height}`);
  processing.push(`q:${quality}`);
  processing.push(`f:${format}`);

  const encodedSource = stringToBase64Url(source);
  const path = `/${processing.join('/')}/${encodedSource}`;

  const key = hexToBytes(keyHex);
  const salt = hexToBytes(saltHex);
  const toSign = new Uint8Array(salt.length + path.length);
  toSign.set(salt, 0);
  toSign.set(new TextEncoder().encode(path), salt.length);
  const signature = bytesToBase64Url(hmac(sha256, key, toSign));

  const trimmedBase = baseUrl.replace(/\/+$/, '');
  return `${trimmedBase}/${signature}${path}`;
};
